import { Injectable, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  filter,
  fromEvent,
  merge,
  Subject,
  Subscription,
  take,
  takeUntil,
} from 'rxjs';

import { ContextMenuComponent } from './context-menu.component';
import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import { KeyInteractionService } from '../interaction';
import {
  ContextMenuServiceKeyActionName,
  ContextMenuServiceKeyActionNameList,
  HotKey,
  HotKeySettings,
} from '@viewer/data';

@Injectable()
export class ContextMenuService {
  private _overlayRef: OverlayRef | null = null;
  private _clickSubscription: Subscription | null = null;
  private _viewContainerRef: ViewContainerRef | null = null;
  private _unsubscribe$!: Subject<void>;

  private isContextMenuOpen: boolean = false;

  constructor(
    private _overlay: Overlay,
    private _stateManagerProjectService: StateManagerProjectService,
    private _keyInteractionService: KeyInteractionService
  ) {}

  public init(viewContainerRef: ViewContainerRef): void {
    this._unsubscribe$ = new Subject<void>();
    this._viewContainerRef = viewContainerRef;
    this._initializeHotKeys();
  }

  public destroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.contextMenuFlag = false;
    this.closeMenu();
    this._viewContainerRef = null;
    this._keyInteractionService.removeKeySettingsList([
      ...ContextMenuServiceKeyActionNameList,
    ]);
  }

  public openMenu({ x, y }: MouseEvent, useHotKeys = true): void {
    this.contextMenuFlag = true;
    this.closeMenu();
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo({ x, y })
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
        },
      ]);

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy: this._overlay.scrollStrategies.close(),
    });

    const componentRef = this._overlayRef.attach(
      new ComponentPortal(ContextMenuComponent, this._viewContainerRef)
    );

    if (useHotKeys) {
      this._keyInteractionService.setActivateState<ContextMenuServiceKeyActionName>(
        'CloseContextMenu',
        true
      );
    }
    componentRef.instance.closeContextMenu.subscribe(() => {
      this.contextMenuFlag = false;
      this.closeMenu();
    });

    this._clickSubscription = merge(
      fromEvent<MouseEvent>(document, 'click'),
      fromEvent(document, 'contextmenu')
    )
      .pipe(
        filter((event) => {
          const clickTarget = event.target as HTMLElement;

          return (
            !!this._overlayRef &&
            !(
              this._overlayRef.overlayElement.contains(clickTarget) ||
              this._stateManagerProjectService.currentSelectedWindowElement?.contains(
                clickTarget
              )
            )
          );
        }),
        take(1)
      )
      .subscribe(() => {
        this.contextMenuFlag = false;
        this.closeMenu()
      });
  }

  public closeMenu(): void {
    this._clickSubscription && this._clickSubscription.unsubscribe();
    this._keyInteractionService.setActivateState<ContextMenuServiceKeyActionName>(
      'CloseContextMenu',
      false
    );
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }

  private _initializeHotKeys(): void {
    // Escape hot key has several bindings and the action for closing context menu currently has the highest priority (4)
    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<ContextMenuServiceKeyActionName>({
          keyActionName: 'CloseContextMenu',
          priority: 4,
          isActive: false,
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.closeMenu();
      });
  }

  set contextMenuFlag(isOpen: boolean) {
    this.isContextMenuOpen = isOpen;
  }

  get contextMenuFlag(): boolean {
    return this.isContextMenuOpen;
  }
}
